import React, { useEffect, useState, useRef } from 'react';
import { motion, useMotionValue, useTransform, useAnimation, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import ComingSoon from '../ComingSoon';
import LikeAnimation from '../LikeAnimation';
import { PortfolioItem } from '../../data/portfolioData';
interface MobileCardProps {
  project: PortfolioItem;
  onSwipe: (direction: 'left' | 'right', project: PortfolioItem) => void;
  isActive: boolean;
  stackIndex: number;
  totalCards: number;
  isPlaceholder?: boolean;
}
const MobileCard: React.FC<MobileCardProps> = ({
  project,
  onSwipe,
  isActive,
  stackIndex,
  totalCards,
  isPlaceholder = false
}) => {
  const navigate = useNavigate();
  const x = useMotionValue(0);
  const controls = useAnimation();
  const rotate = useTransform(x, [-300, 0, 300], [-30, 0, 30]);
  const scale = useTransform(x, [-300, 0, 300], [0.8, 1, 0.8]);
  const opacity = useTransform(x, [-200, 0, 200], [0.5, 1, 0.5]);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const lastTapRef = useRef<number>(0);
  const tapTimeoutRef = useRef<NodeJS.Timeout>();
  const handleDragEnd = async (event: any, info: any) => {
    if (!isActive) return;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const isSwipe = Math.abs(velocity) > 250 || Math.abs(offset) > 100;
    if (isSwipe) {
      const direction = offset > 0 ? 'right' : 'left';
      const velocityMultiplier = Math.min(Math.abs(velocity) / 1000, 1);
      await controls.start({
        x: direction === 'right' ? window.innerWidth + 200 : -window.innerWidth - 200,
        rotate: direction === 'right' ? 45 * velocityMultiplier : -45 * velocityMultiplier,
        opacity: 0,
        transition: {
          type: 'spring',
          damping: 20,
          stiffness: 300,
          velocity: info.velocity.x * 0.1
        }
      });
      onSwipe(direction, project);
    } else {
      controls.start({
        x: 0,
        rotate: 0,
        opacity: 1,
        transition: {
          type: 'spring',
          damping: 15,
          stiffness: 150,
          velocity: info.velocity.x * 0.1
        }
      });
    }
  };
  const handleTap = () => {
    if (!isActive) return;
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    if (lastTapRef.current && now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      clearTimeout(tapTimeoutRef.current);
      setShowLikeAnimation(true);
      setTimeout(() => setShowLikeAnimation(false), 800);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
      tapTimeoutRef.current = setTimeout(() => {
        if (!isActive) return;
        const slug = project.title.toLowerCase().replace(/\s+/g, '-');
        navigate(`/project/${slug}`);
      }, DOUBLE_TAP_DELAY);
    }
  };
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
    };
  }, []);
  const getStackStyles = () => {
    const maxVisibleCards = 3;
    const visibleIndex = Math.min(stackIndex, maxVisibleCards);
    const scale = 1 - visibleIndex * 0.08;
    const translateY = visibleIndex * 25;
    const translateZ = -visibleIndex * 10;
    const opacity = 1 - visibleIndex * 0.25;
    const zIndex = 10 - visibleIndex;
    return {
      position: 'absolute',
      width: '100%',
      height: '100%',
      zIndex,
      transform: `translateY(${translateY}px) translateZ(${translateZ}px) scale(${scale})`,
      opacity: stackIndex === 0 ? 1 : opacity
    };
  };
  return <motion.div style={getStackStyles() as any} animate={controls} initial={stackIndex === 0 ? {
    scale: 0.95,
    opacity: 0
  } : false} animate={stackIndex === 0 ? {
    scale: 1,
    opacity: 1
  } : controls} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30,
    mass: 0.8
  }} className={`${isActive && !project.comingSoon ? 'cursor-grab active' : ''}`} onClick={handleTap} data-id="element-139">
      <motion.div className="relative w-full h-full rounded-3xl overflow-hidden bg-white shadow-xl" style={{
      x: isActive ? x : 0,
      rotate: isActive ? rotate : 0,
      scale: isActive ? scale : undefined,
      opacity: stackIndex === 0 ? 1 : undefined,
      boxShadow: `0 ${12 + stackIndex * 6}px ${20 + stackIndex * 10}px -${stackIndex * 3}px rgba(0,0,0,${0.25 - stackIndex * 0.05})`,
      willChange: 'transform'
    }} drag={isActive ? 'x' : false} dragConstraints={{
      left: 0,
      right: 0
    }} dragElastic={0.1} onDragEnd={handleDragEnd} whileTap={{
      cursor: 'grabbing',
      scale: 0.98
    }} data-id="element-140">
        <div className="absolute inset-0 flex flex-col" data-id="element-141">
          <div className="relative flex-1" data-id="element-142">
            <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-143" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-transparent" data-id="element-144" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 flex flex-col gap-4" data-id="element-145">
            <h2 className="text-2xl font-bold text-white leading-tight" data-id="element-146">
              {project.title}
            </h2>
            <p className="text-white/90 text-sm line-clamp-2 mb-2" data-id="element-147">
              {project.shortDescription}
            </p>
            <div className="flex flex-wrap items-center gap-2" data-id="element-148">
              <span className={`px-3 py-1 rounded-full text-xs font-medium
                ${project.category === 'Video' ? 'bg-indigo-500/90 text-white' : project.category === 'Photo' ? 'bg-rose-500/90 text-white' : 'bg-violet-500/90 text-white'}`} data-id="element-149">
                {Array.isArray(project.category) ? project.category[0] : project.category}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white" data-id="element-150">
                {project.year}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white truncate max-w-[150px]" data-id="element-151">
                {project.client}
              </span>
            </div>
          </div>
        </div>
        {isActive && <>
            <motion.div className="absolute top-1/2 right-6 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg" style={{
          opacity: useTransform(x, [-50, 0, 50, 150], [0, 0, 0.5, 1]),
          scale: useTransform(x, [-50, 0, 50, 150], [0.8, 1, 1.2, 1.4]),
          x: useTransform(x, [-50, 0, 50, 150], [0, 0, 10, 20])
        }} data-id="element-152">
              <ArrowRightIcon className="w-6 h-6 text-green-500" data-id="element-153" />
            </motion.div>
            <motion.div className="absolute top-1/2 left-6 transform -translate-y-1/2 bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg" style={{
          opacity: useTransform(x, [-150, -50, 0, 50], [1, 0.5, 0, 0]),
          scale: useTransform(x, [-150, -50, 0, 50], [1.4, 1.2, 1, 0.8]),
          x: useTransform(x, [-150, -50, 0, 50], [-20, -10, 0, 0])
        }} data-id="element-154">
              <ArrowLeftIcon className="w-6 h-6 text-rose-500" data-id="element-155" />
            </motion.div>
          </>}
      </motion.div>
      {project.comingSoon && <ComingSoon data-id="element-156" />}
      <LikeAnimation isVisible={showLikeAnimation} data-id="element-157" />
    </motion.div>;
};
export default MobileCard;